import { canonicalJson } from './canonical-json';
import type { GateCustomAttributes, GateIdentifiers } from './envelope';

export interface GateProfile {
	/** The Statsig target app the values were evaluated for, e.g. `rovo-extension_web`. */
	readonly targetApp: string;
	readonly identifiers: GateIdentifiers;
	readonly customAttributes: GateCustomAttributes;
}

/**
 * SHA-256 (hex) over the canonical JSON of `{ targetApp, identifiers, customAttributes }`, so the
 * receiver can tell one profile's envelope from another's without storing any identity. Computed by
 * the producer only; the chrome layer never re-derives it, having nothing to derive it from.
 *
 * A deliberate fork of the polling provider's private `createHash`, which is not exported and keys
 * on the provider's own profile shape rather than this contract.
 *
 * Throws where WebCrypto is absent, which includes a content script on a plain `http://` page, so
 * callers must handle it rather than assume it resolves.
 */
export async function computeProfileHash(profile: GateProfile): Promise<string> {
	if (!globalThis.crypto?.subtle) {
		throw new Error(
			'Feature gates: WebCrypto subtle is unavailable in this context (insecure origin?)',
		);
	}
	const input = canonicalJson({
		targetApp: profile.targetApp,
		identifiers: profile.identifiers,
		customAttributes: profile.customAttributes,
	});
	const bytes = new TextEncoder().encode(input);
	const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
	return toHex(new Uint8Array(digest));
}

function toHex(bytes: Uint8Array): string {
	let out = '';
	for (const byte of bytes) {
		out += byte.toString(16).padStart(2, '0');
	}
	return out;
}
